const DEFAULT_DEDUPE_WINDOW_MS = 1500
const inFlightFetchRequests = new Map()
const patchedAxiosInstances = new WeakSet()

const normalizeUrl = (input) => {
	if (typeof input === 'string') return input
	return input?.url || String(input)
}

const stableSerialize = (value) => {
	if (!value) return ''

	if (typeof FormData !== 'undefined' && value instanceof FormData) {
		const entries = []
		value.forEach((entryValue, key) => {
			if (entryValue instanceof File) {
				entries.push([key, entryValue.name, entryValue.size, entryValue.type])
			} else {
				entries.push([key, entryValue])
			}
		})
		return JSON.stringify(entries)
	}

	if (typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams) {
		return value.toString()
	}

	if (typeof value === 'string') return value

	try {
		return JSON.stringify(value, Object.keys(value).sort())
	} catch (error) {
		return String(value)
	}
}

const getFetchRequestKey = (input, init = {}) => {
	const method = (init.method || input?.method || 'GET').toUpperCase()
	const url = normalizeUrl(input)
	const body = init.body || input?.body || ''
	return `${method}:${url}:${stableSerialize(body)}`
}

const getAxiosRequestKey = (config = {}) => {
	const method = (config.method || 'GET').toUpperCase()
	const baseURL = config.baseURL || ''
	const url = config.url || ''
	const params = stableSerialize(config.params)
	const data = stableSerialize(config.data)
	return `${method}:${baseURL}:${url}:${params}:${data}`
}

const shouldDedupeMethod = (method = 'GET') => {
	return ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())
}

export const installGlobalFetchRequestDeduper = ({windowMs = DEFAULT_DEDUPE_WINDOW_MS} = {}) => {
	if (typeof window === 'undefined' || !window.fetch || window.fetch.__requestDeduperInstalled) {
		return
	}

	const originalFetch = window.fetch.bind(window)

	const dedupedFetch = (input, init = {}) => {
		const method = (init.method || input?.method || 'GET').toUpperCase()
		if (!shouldDedupeMethod(method)) {
			return originalFetch(input, init)
		}

		const key = getFetchRequestKey(input, init)
		const current = inFlightFetchRequests.get(key)

		if (current) {
			return current.promise.then((response) => response.clone())
		}

		const requestPromise = originalFetch(input, init).finally(() => {
			window.setTimeout(() => inFlightFetchRequests.delete(key), windowMs)
		})

		inFlightFetchRequests.set(key, {promise: requestPromise})
		return requestPromise.then((response) => response.clone())
	}

	dedupedFetch.__requestDeduperInstalled = true
	dedupedFetch.__originalFetch = originalFetch
	window.fetch = dedupedFetch
}

export const installAxiosRequestDeduper = (axiosInstance, {windowMs = DEFAULT_DEDUPE_WINDOW_MS} = {}) => {
	if (!axiosInstance || patchedAxiosInstances.has(axiosInstance)) return axiosInstance

	const inFlightAxiosRequests = new Map()
	const originalRequest = axiosInstance.request.bind(axiosInstance)

	const requestWithDeduper = (configOrUrl, maybeConfig = {}) => {
		const config = typeof configOrUrl === 'string'
			? {...maybeConfig, url: configOrUrl}
			: {...configOrUrl}
		const method = (config.method || 'GET').toUpperCase()

		if (!shouldDedupeMethod(method) || config.skipRequestDedupe) {
			return originalRequest(configOrUrl, maybeConfig)
		}

		const key = getAxiosRequestKey(config)
		const current = inFlightAxiosRequests.get(key)
		if (current) return current

		const requestPromise = originalRequest(configOrUrl, maybeConfig).finally(() => {
			setTimeout(() => inFlightAxiosRequests.delete(key), windowMs)
		})
		inFlightAxiosRequests.set(key, requestPromise)
		return requestPromise
	}

	axiosInstance.request = requestWithDeduper

	;['get', 'delete', 'head', 'options'].forEach((method) => {
		const originalMethod = axiosInstance[method]?.bind(axiosInstance)
		if (!originalMethod) return
		axiosInstance[method] = (url, config = {}) => {
			return requestWithDeduper({...config, method, url})
		}
	})

	;['post', 'put', 'patch'].forEach((method) => {
		const originalMethod = axiosInstance[method]?.bind(axiosInstance)
		if (!originalMethod) return
		axiosInstance[method] = (url, data, config = {}) => {
			return requestWithDeduper({...config, method, url, data})
		}
	})

	patchedAxiosInstances.add(axiosInstance)
	return axiosInstance
}
